import { create } from "zustand";

export interface Folder {
  id: string;
  name: string;
  type: "system" | "custom";
  icon: string;
  unreadCount: number;
  totalCount: number;
  parentId?: string;
}

interface FolderState {
  folders: Folder[];
  activeFolderId: string | null;
  isLoading: boolean;
  error: string | null;

  setFolders: (folders: Folder[]) => void;
  setActiveFolder: (id: string | null) => void;
  updateUnreadCount: (folderId: string, count: number) => void;
  addFolder: (folder: Folder) => void;
  removeFolder: (id: string) => void;
  renameFolder: (id: string, name: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

// Mismo conjunto y orden canónico que produce normalizeFolders() para evitar
// el "brinco" del sidebar al cargar (el primer paint ya debe coincidir con el
// orden real del servidor: Archive, Drafts, INBOX, Junk, Sent, Trash).
const DEFAULT_FOLDERS: Folder[] = [
  { id: "INBOX", name: "Bandeja de entrada", type: "system", icon: "inbox", unreadCount: 0, totalCount: 0 },
  { id: "Enviados", name: "Enviados", type: "system", icon: "send", unreadCount: 0, totalCount: 0 },
  { id: "Borradores", name: "Borradores", type: "system", icon: "file-text", unreadCount: 0, totalCount: 0 },
  { id: "Spam", name: "Spam", type: "system", icon: "alert-triangle", unreadCount: 0, totalCount: 0 },
  { id: "Archivo", name: "Archivo", type: "system", icon: "folder", unreadCount: 0, totalCount: 0 },
  { id: "Papelera", name: "Papelera", type: "system", icon: "trash-2", unreadCount: 0, totalCount: 0 },
];

export const useFolderStore = create<FolderState>()((set, get) => ({
  folders: DEFAULT_FOLDERS,
  activeFolderId: "INBOX",
  isLoading: false,
  error: null,

  setFolders: (folders) => set({ folders }),

  setActiveFolder: (id) => set({ activeFolderId: id }),

  updateUnreadCount: (folderId, count) =>
    set((state) => ({
      folders: state.folders.map((f) =>
        f.id === folderId ? { ...f, unreadCount: count } : f,
      ),
    })),

  addFolder: (folder) =>
    set((state) => ({
      folders: [...state.folders, folder],
    })),

  removeFolder: (id) =>
    set((state) => ({
      folders: state.folders.filter((f) => f.id !== id),
    })),

  renameFolder: (id, name) =>
    set((state) => ({
      folders: state.folders.map((f) =>
        f.id === id ? { ...f, name } : f,
      ),
    })),

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  reset: () =>
    set({
      folders: DEFAULT_FOLDERS,
  activeFolderId: "INBOX",
      isLoading: false,
      error: null,
    }),
}));
